package com.crowpay.views.theming

import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.views.components.nav.windowIsNarrow
import com.crowpay.views.components.space
import com.crowpay.views.components.unreadIconWhen
import com.lightningkite.kiteui.ViewWrapper
import com.lightningkite.kiteui.models.*
import com.lightningkite.kiteui.reactive.ReactiveContext
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.views.*
import com.lightningkite.kiteui.views.direct.*
import kotlin.time.Duration.Companion.milliseconds

data object PrimaryButtonSemantic: Semantic {
    override val key: String = "priBtn"
    override fun default(theme: Theme): ThemeAndBack = theme[ButtonSemantic].theme.copy(
        id = key,
        background = AppColors.secondary.main,
        foreground = Color.white,
        outlineWidth = 0.dp,
        derivations = mapOf(
            HoverSemantic to {
                it
                    .copy(background = AppColors.primaryButtonHover)
                    .withBack
            },
            DownSemantic to {
                it
                    .copy(background = AppColors.secondary.dark)
                    .withBack
            },
        )
    ).withBack
}

@ViewModifierDsl3
val ViewWriter.primaryButton: ViewWrapper get() = PrimaryButtonSemantic.onNext

object SecondaryButtonSemantic : Semantic {
    override val key: String = "secBtn"
    override fun default(theme: Theme): ThemeAndBack {
        val backIsDark = theme.background.closestColor().toHSP().brightness < 0.95

        return theme[ButtonSemantic].theme.copy(
            id = key,
            background = if (backIsDark) AppColors.grey.light3 else AppColors.grey.light2,
            foreground = Color.black,
            derivations = mapOf(
                HoverSemantic to {
                    it
                        .copy(background = if (backIsDark) AppColors.grey.light2 else AppColors.grey.light1)
                        .withBack
                },
                DownSemantic to {
                    it
                        .copy(background = if (backIsDark) AppColors.grey.light1 else AppColors.grey.main)
                        .withBack
                },
            )
        ).withBack
    }
}

@ViewModifierDsl3
val ViewWriter.secondaryButton: ViewWrapper get() = SecondaryButtonSemantic.onNext

data object TertiaryButtonSemantic: Semantic {
    override val key: String = "terBtn"
    override fun default(theme: Theme): ThemeAndBack = theme[ButtonSemantic].theme.copy(
        id = key,
        foreground = AppColors.primary.main,
        outline = AppColors.primary.main,
        outlineWidth = AppDimensions.outlineWidth,
//        derivations = mapOf(
//            HoverSemantic to { it.withoutBack }
//        )
    ).withBack
}

@ViewModifierDsl3
val ViewWriter.tertiaryButton: ViewWrapper get() = TertiaryButtonSemantic.onNext

data object DangerButtonSemantic: Semantic {
    override val key: String = "wrnBtn"
    override fun default(theme: Theme): ThemeAndBack = theme[ButtonSemantic].theme.copy(
        id = key,
        background = AppColors.red.main,
        foreground = Color.white,
    ).withBack
}

@ViewModifierDsl3
val ViewWriter.dangerButton: ViewWrapper get() = DangerButtonSemantic.onNext

fun WarningButtonSemantic() = ButtonSemantic + WarningSemantic

data object CreamButtonSemantic: Semantic {
    override val key: String = "crmBtn"
    override fun default(theme: Theme): ThemeAndBack = theme[ButtonSemantic].theme.copy(
        id = key,
        background = AppColors.accent.light2,
        foreground = Color.black
    ).withBack
}

@ViewModifierDsl3
val ViewWriter.creamButton: ViewWrapper get() = CreamButtonSemantic.onNext

data object TextButtonSemantic: Semantic {
    override val key: String = "txtBtn"
    override fun default(theme: Theme): ThemeAndBack = theme[ButtonSemantic].theme.copy(
        id = key,
        foreground = Color.black,
        outlineWidth = 0.dp,
        derivations = mapOf(
            HoverSemantic to {
                it.copy(
                    font = it.font.copy(underline = true),
                ).withoutBack
            }
        )
    ).withBack
}

@ViewModifierDsl3
val ViewWriter.textButton: ViewWrapper get() = TextButtonSemantic.onNext

@ViewModifierDsl3
val ViewWriter.warningTextButton: ViewWrapper get() = (TextButtonSemantic + ForegroundSemantic(AppColors.red.main)).onNext

data object DeleteButtonSemantic: Semantic {
    override val key: String = "dltBtn"
    override fun default(theme: Theme): ThemeAndBack = theme[TextButtonSemantic].theme.copy(
        id = key,
        transitionDuration = 100.milliseconds,
        derivations = mapOf(
            HoverSemantic to {
                it.copy(
                    foreground = AppColors.red.main,
                    font = it.font.copy(underline = true)
                ).withoutBack
            }
        )
    ).withoutBack
}

@ViewModifierDsl3
val ViewWriter.deleteButton: ViewWrapper get() = DeleteButtonSemantic.onNext

data object ExpandButtonSemantic: Semantic {
    override val key: String = "expdBtn"
    override fun default(theme: Theme): ThemeAndBack = theme[ButtonSemantic].theme.copy(
        id = key,
        transitionDuration = 100.milliseconds,
        derivations = mapOf(
            HoverSemantic to {
                it.copy(
                    id = "expdHvr",
                    background = AppColors.grey.light2
                ).withBack
            },
            DownSemantic to {
                it.copy(
                    id = "expdDwn",
                    background = AppColors.grey.light1
                ).withBack
            }
        )
    ).withoutBack
}

@ViewModifierDsl3
val ViewWriter.expandButtonTheme: ViewWrapper get() = ExpandButtonSemantic.onNext

object RemoveToggleSemantic : Semantic {
    override val key: String = "rmvtgl"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        derivations = mapOf (
            HoverSemantic to { it.withoutBack },
            UnselectedSemantic to { it.withoutBack },
            SelectedSemantic to { it.withoutBack },
            DownSemantic to { it.withoutBack },
            DisabledSemantic to {
                it.copy(
                    id = "dis",
                    foreground = it.foreground.applyAlpha(alpha = 0.25f),
                    outline = it.outline.applyAlpha(alpha = 0.25f),
                ).withoutBack
            }
        )
    ).withoutBack
}

object RemoveOutlineSemantic : Semantic {
    override val key: String = "rmvbdr"
    override fun default(theme: Theme): ThemeAndBack = theme.copy(
        id = key,
        outlineWidth = 0.rem
    ).withBack
}

fun RView.specCenteredText(content: String) =
    sizeConstraints(AppDimensions.buttonMinimumWidth) - row {
        spacing = 0.px
        expanding - space(AppDimensions.buttonHorizontalSpacing)
        centered - body(content)
        expanding - space(AppDimensions.buttonHorizontalSpacing)
    }

fun RView.specCenteredText(message: ReactiveContext.() -> String) {
    sizeConstraints(AppDimensions.buttonMinimumWidth) - row {
        spacing = 0.px
        expanding - space(AppDimensions.buttonHorizontalSpacing)
        centered - body { ::content.invoke(message) }
        expanding - space(AppDimensions.buttonHorizontalSpacing)
    }
}

fun RView.specCenteredContent(removeSpacesWhenNarrow: Boolean = false, setup: RowOrCol.() -> Any?): ViewModifiable {
    if (removeSpacesWhenNarrow) changingSizeConstraints {
        if (windowIsNarrow()) SizeConstraints() else SizeConstraints(minWidth = AppDimensions.buttonMinimumWidth)
    }
    else sizeConstraints(minWidth = AppDimensions.buttonMinimumWidth)
    return row {
        spacing = 0.px
        expanding - space(AppDimensions.buttonHorizontalSpacing) {
            if (removeSpacesWhenNarrow) existsDefaultFalse { !windowIsNarrow() }
        }
        this.setup()
        expanding - space(AppDimensions.buttonHorizontalSpacing) {
            if (removeSpacesWhenNarrow) existsDefaultFalse { !windowIsNarrow() }
        }
    }
}

fun RView.specCenteredTextWithUnread(content: String, unread: ReactiveContext.() -> Boolean) =
    specCenteredContent {
        row {
            spacing = AppDimensions.expandButtonSpace
            centered - unreadIconWhen { unread() }
            centered - body(content)
        }
    }


fun RView.specCenteredTextWithUnread(message: ReactiveContext.() -> String, unread: ReactiveContext.() -> Boolean) =
    specCenteredContent {
        row {
            spacing = AppDimensions.expandButtonSpace
            centered - unreadIconWhen { unread() }
            centered - body { ::content.invoke(message) }
        }
    }


@ViewDsl
fun ViewWriter.textButton(content: String, setup: Button.() -> Unit) {
    textButton - button {
        specCenteredText(content)
        setup()
    }
}


data object MessageButton : Semantic {
    override val key: String = "msgbtn"
    override fun default(theme: Theme): ThemeAndBack = theme[ButtonSemantic].theme.copy(
        id = key,
        background = AppColors.primary.main.matchAlpha(theme.background),
        foreground = Color.white.matchAlpha(theme.foreground),
    ).withBack
}